import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import TeknikAuthService from '../services/teknik-auth-service.js';
import { teknikTokens, teknikButtons } from '../styles/teknik-tokens.js';
import { tkPrimitives } from '../styles/tk-primitives.js';
import { tkAuthCard, discordMark } from '../styles/tk-auth.js';

@customElement('app-login')
export class AppLogin extends LitElement {
  @state() tab: 'teknik' | 'discord' = 'teknik';
  @state() email = '';
  @state() password = '';
  @state() error = '';
  @state() loading = false;
  @state() checkingSession = true;
  @state() existingUser: any = null;

  /**
   * Where to go after signing in.
   *
   * Only same-origin paths are honoured: anything starting with `//` or
   * carrying a scheme is an open redirect, which turns the login page into a
   * credible phishing hop. Everything that fails the check falls back to '/'.
   */
  private returnPath(): string {
    const raw = new URLSearchParams(window.location.search).get('return') || '';
    if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
    return raw;
  }

  async connectedCallback() {
    super.connectedCallback();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auth_error') === 'true' || urlParams.get('error')) {
      this.error = "That sign-in didn't complete. Try again.";
    }
    // Check if already logged in
    try {
      const res = await fetch('/api/auth/status', { credentials: 'include' });
      const data = await res.json();
      if (data.authenticated && !urlParams.get('switch')) {
        window.location.href = this.returnPath();
        return;
      }
      this.existingUser = data.authenticated ? data.user : null;
    } catch {}
    this.checkingSession = false;
  }

  async handleTeknikLogin(e: Event) {
    e.preventDefault();
    this.error = '';
    this.loading = true;
    const result = await TeknikAuthService.login(this.email, this.password);
    this.loading = false;
    if (result.success) {
      if ((result as any).requiresTwoFactor) {
        // Preserve the destination across the 2FA hop.
        const back = this.returnPath();
        window.location.href = back === '/' ? '/2fa' : `/2fa?return=${encodeURIComponent(back)}`;
      } else {
        window.location.href = this.returnPath();
      }
    } else {
      this.error = result.error || "That email and password didn't match.";
    }
  }

  handleDiscordLogin(e: MouseEvent) {
    e.preventDefault();
    const back = this.returnPath();
    window.location.href = back === '/' ? '/auth/discord' : `/auth/discord?return=${encodeURIComponent(back)}`;
  }

  static styles = [
    teknikTokens,
    teknikButtons,
    tkPrimitives,
    tkAuthCard,
    css`
      .tabs {
        display: flex;
        gap: 0.4rem;
        margin-bottom: 1.25rem;
      }
      .tab-btn {
        flex: 1;
        padding: 0.5rem 0.9rem;
        border-radius: var(--tk-r-control);
        border: 1px solid var(--tk-border);
        background: transparent;
        color: var(--tk-muted);
        font-family: var(--font-family);
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
      }
      .tab-btn:hover { color: var(--tk-text); border-color: var(--tk-border-strong); }
      .tab-btn.active {
        background: var(--tk-accent-soft);
        border-color: var(--tk-accent-line);
        color: var(--tk-accent);
        font-weight: 600;
      }

      .continue {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        flex-wrap: wrap;
        padding: 0.75rem 0.95rem;
        margin-bottom: 1.25rem;
        border: 1px solid var(--tk-border);
        border-radius: var(--tk-r-control);
        background: var(--tk-panel-2);
        font-size: 0.875rem;
        color: var(--tk-muted);
      }
      .continue strong { color: var(--tk-text); margin-left: 0.35rem; }

      .forgot { text-align: right; margin-top: -0.35rem; }
      .forgot a { font-size: 0.8125rem; color: var(--tk-dim); text-decoration: none; }
      .forgot a:hover { color: var(--tk-accent); }

      .discord-copy {
        font-size: 0.9rem;
        color: var(--tk-muted);
        line-height: 1.6;
        margin-bottom: 1rem;
      }
    `,
  ];

  render() {
    if (this.checkingSession) {
      return html`<main><div class="card"><div class="tk-empty">Checking your session…</div></div></main>`;
    }

    return html`
      <main>
        <div class="card">
          <div class="eyebrow">Sign in</div>
          <h1 class="title">Welcome back</h1>
          <p class="sub">Use your Teknik account, or continue with Discord.</p>

          ${this.existingUser
            ? html`<div class="continue">
                <span>Already signed in as<strong>${this.existingUser.username}</strong></span>
                <a class="tk-btn secondary" href="/">Continue</a>
              </div>`
            : ''}

          <div class="tabs">
            <button
              class="tab-btn ${this.tab === 'teknik' ? 'active' : ''}"
              @click=${() => { this.tab = 'teknik'; this.error = ''; }}
            >Teknik account</button>
            <button
              class="tab-btn ${this.tab === 'discord' ? 'active' : ''}"
              @click=${() => { this.tab = 'discord'; this.error = ''; }}
            >Discord</button>
          </div>

          ${this.error ? html`<div class="tk-flash err" style="margin-bottom:1rem">${this.error}</div>` : ''}

          ${this.tab === 'teknik'
            ? html`
                <form @submit=${this.handleTeknikLogin}>
                  <div class="field">
                    <label class="tk-label" for="email">Email</label>
                    <input
                      id="email" class="tk-input" type="email" placeholder="you@example.com"
                      required autocomplete="email"
                      .value=${this.email}
                      @input=${(e: any) => (this.email = e.target.value)}
                    />
                  </div>
                  <div class="field">
                    <label class="tk-label" for="pw">Password</label>
                    <input
                      id="pw" class="tk-input" type="password"
                      required autocomplete="current-password"
                      .value=${this.password}
                      @input=${(e: any) => (this.password = e.target.value)}
                    />
                  </div>
                  <div class="forgot"><a href="/forgot-password">Forgot your password?</a></div>
                  <button class="tk-btn primary full" type="submit" ?disabled=${this.loading}>
                    ${this.loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
                <div class="foot">Don't have an account? <a href="/register">Create one</a></div>
              `
            : html`
                <div class="discord-copy">
                  Sign in with Discord to manage your servers and Luna.
                </div>
                <a href="/auth/discord" class="discord-btn" @click=${this.handleDiscordLogin}>
                  ${discordMark} Continue with Discord
                </a>
                <div class="divider">or</div>
                <a class="tk-btn secondary full" href="https://discord.gg/quFm2aeayP" target="_blank" rel="noopener">
                  Join our Discord
                </a>
              `}
        </div>
      </main>
    `;
  }
}
